/**
 * Phase 5.9.14 — Reset Password page.
 *
 * Reads `?token=...` from URL. POSTs to /api/auth/reset-password with the
 * token + new password. On success, the backend has already set fresh
 * cookies — we redirect to the appropriate dashboard.
 */
import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Lock, ArrowLeft } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (pw !== pw2) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/reset-password`,
        { token, new_password: pw },
        { withCredentials: true },
      );
      setDone(true);
      toast.success('Password reset. Signing you in…');
      await checkAuth();
      // Fetch /me directly so we know the role to route to (checkAuth sets state async)
      let dest = '/customer';
      try {
        const { data: me } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        const role = me?.role;
        dest = role === 'driver' ? '/driver'
          : (role === 'dispatcher' || role === 'super_admin') ? '/dispatch'
          : '/customer';
      } catch { /* fall back to /customer */ }
      setTimeout(() => navigate(dest, { replace: true }), 800);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Reset failed. Request a new link.';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050003] p-4">
        <div className="w-full max-w-md bg-[#0C0612] border border-[#FF3B30] p-6 rounded">
          <h1 className="text-[#FF3B30] font-mono text-sm mb-2">// ERROR</h1>
          <p className="text-white" data-testid="reset-no-token">No reset token in URL. Please use the link from your email.</p>
          <Link to="/forgot-password" className="inline-flex items-center text-[#39FF14] hover:underline text-sm mt-4 font-mono">
            <ArrowLeft className="w-4 h-4 mr-1" /> Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050003] p-4">
      <div className="w-full max-w-md bg-[#0C0612] border border-[#2A0F3D] p-6 rounded">
        <h1 className="text-[#39FF14] font-mono text-sm mb-2">// ASH & MERCH</h1>
        <h2 className="text-white text-xl mb-4" data-testid="reset-page-title">Set a new password</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="New password (8+ chars)"
            required
            minLength={8}
            autoFocus
            className="bg-black/40 border-[#2A0F3D] text-white"
            data-testid="reset-new-password"
          />
          <Input
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            placeholder="Confirm new password"
            required
            minLength={8}
            className="bg-black/40 border-[#2A0F3D] text-white"
            data-testid="reset-confirm-password"
          />
          <Button
            type="submit"
            disabled={loading || done || !pw || !pw2}
            className="w-full bg-[#39FF14] text-black hover:bg-[#4AF626] font-bold rounded-none"
            data-testid="reset-submit-btn"
          >
            <Lock className="w-4 h-4 mr-2" />
            {loading ? 'RESETTING…' : done ? 'DONE — REDIRECTING' : 'SET NEW PASSWORD'}
          </Button>
        </form>
      </div>
    </div>
  );
}
